export const QuestionCodes = {
  Department: 'Department',
  TeamRole: 'Team_Role',
  Goal: 'Goal',
  TeamSizeSurvey: 'Team_Size_Survey',
  Industry: 'Industry',
  Source: 'Source',
} as const;

type QuestionCode = typeof QuestionCodes[keyof typeof QuestionCodes];

export type Answers = Partial<Record<QuestionCode, string>>;

export type Question = {
  code: QuestionCode;
  title: string;
  inlineSubtitle?: string;
  optional?: boolean;
  // Kept in the config (and in the stored answers) but not presented to users
  // any more — the main survey is trimmed to three questions (PRE-3134).
  hidden?: boolean;
  options: {
    label: string;
    // Persisted verbatim in `marketing_data` and pushed to Mixpanel user and
    // company profiles, so it outlives the label. When an option is reworded,
    // change `label` and leave `value` alone — renaming it splits every
    // historical segment instead of reading as a rewording (PRE-2868).
    value: string;
    input?: boolean;
  }[];
};

export const SKIPPED_ANSWER = 'Not answered';

const QUESTIONS: Question[] = [
  {
    code: QuestionCodes.Department,
    title: 'How would you describe your job?',
    inlineSubtitle: '(Select one)',
    optional: false,
    options: [
      { label: 'SEO', value: 'SEO' },
      { label: 'Developer', value: 'Developer' },
      { label: 'Marketing', value: 'Marketing' },
      { label: 'Product', value: 'Product' },
      { label: 'Founder / Owner', value: 'Founder' },
      { label: 'Executive / Leadership', value: 'Executive / Leadership' },
      { label: 'Other (please specify)', value: 'Other', input: true },
    ],
  },
  {
    code: QuestionCodes.TeamRole,
    title: 'What is your role?',
    inlineSubtitle: '(Select one)',
    optional: false,
    hidden: true,
    options: [
      { label: 'Team Member (Individual Contributor)', value: 'Team Member' },
      { label: 'Executive (C-Level, VP, Director)', value: 'Executive' },
      { label: 'Manager / Team Lead', value: 'Manager' },
      { label: 'Other (please specify)', value: 'Other', input: true },
    ],
  },
  {
    code: QuestionCodes.Goal,
    title: 'What is your primary goal with Prerender.io?',
    inlineSubtitle: '(Select one)',
    optional: false,
    options: [
      { label: 'Improve organic search rankings', value: 'Improve organic search rankings' },
      {
        label: 'Make my pages easy for search engines to crawl',
        value: 'Ensure search engines can correctly crawl my pages’ content',
      },
      {
        label: 'Improve my AI search visibility (AI SEO)',
        value: 'Improve your AI search visibility (AI SEO)',
      },
      { label: 'Fix slow page load times for crawlers', value: 'Fix slow page load times for crawlers' },
      { label: 'Reduce reliance on costly workarounds', value: 'Reduce reliance on costly workarounds' },
      { label: 'Other (please specify)', value: 'Other', input: true },
    ],
  },

  {
    code: QuestionCodes.TeamSizeSurvey,
    title: 'How many people will be using Prerender.io?',
    inlineSubtitle: '(Select one)',
    optional: false,
    hidden: true,
    options: [
      { label: 'Just me', value: 'Just me' },
      { label: '2-5', value: '2-5' },
      { label: '6-10', value: '6-10' },
      { label: '10+', value: '10+' },
    ],
  },

  {
    code: QuestionCodes.Industry,
    title: 'What industry does your organization operate in?',
    inlineSubtitle: '(Select one)',
    optional: false,
    hidden: true,
    options: [
      { label: 'E-commerce / Retail / Online Marketplaces', value: 'E-commerce / Retail / Online Marketplaces' },
      { label: 'Marketing / Advertising / Media', value: 'Marketing / Advertising / Media' },
      { label: 'IT / Technology', value: 'IT / Technology' },
      { label: 'Travel / Hospitality', value: 'Travel / Hospitality' },
      { label: 'Other (please specify)', value: 'Other', input: true },
    ],
  },

  {
    code: QuestionCodes.Source,
    title: 'How did you hear about us?',
    inlineSubtitle: '(Select one)',
    optional: false,
    options: [
      { label: 'Social media', value: 'Social media' },
      { label: 'Search engine', value: 'Search engine' },
      { label: 'AI engine', value: 'AI engine' },
      { label: 'Podcast', value: 'Podcast' },
      { label: 'Word of mouth', value: 'Word of mouth' },
      { label: 'Event or trade show', value: 'Event or trade show' },
      { label: 'Other (please specify)', value: 'Other', input: true },
    ],
  },
];

export default QUESTIONS;
